import { useTranslation } from "react-i18next";
import { useContext, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import dossierService, { IDossierAddFile } from "api/dossierService";
import fileService from "api/fileService";
import BinIcon from "assets/icons/bin.svg?react";
import DownloadIcon from "assets/icons/download.svg?react";
import EditIcon from "assets/icons/mode_edit.svg?react";
import CopyIcon from "assets/icons/copyd.svg?react";
import CopyIcon2 from "assets/icons/copy-new.svg?react";
import CutIcon from "assets/icons/cut.svg?react";
import LabelIcon from "assets/icons/label.svg?react";
import StatusIcon from "assets/icons/flag_border.svg?react";
import SignIcon from "assets/icons/sign.svg?react";
import ShareIcon from "assets/icons/share.svg?react";
import AcceptanceSignIcon from "assets/icons/signature.svg?react";
import LettersIcon from "assets/icons/letters.svg?react";

import ConfirmModal from "components/ConfirmModal";
import TransferToAcceptanceModal from "components/TransferToAcceptanceModal";
import DossierFilesContext from "contexts/DossierFilesContext";
import KeysContext from "contexts/KeysContext";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import { IDossierItem } from "dto/Dossier/IDossierItem";
import { ItemKind } from "dto/IKindItems";
import { useAuth } from "react-oidc-context";
import downloadFile from "utils/downloadFile";
import { getUserId } from "utils/getUserId";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NewTabIcon from "assets/icons/new_tab.svg?react";
import { faEllipsisVertical, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import pluginService from "api/pluginService";
import CertContext from "contexts/CertContext";
import { createDossierObject } from "components/CaseDossier/createDossierObject";
import { getBlob } from "utils/getBlob";
import { getFile as GetFile } from "utils/getFile";
import { decryptFile } from "utils/decryptFile";
import { handleCreate } from "utils/uploadFile";
import { axiosInstance } from "api/axios";
import { addCopyToFileName } from "utils/addCopyToFileName";
import DeskIcon from "assets/icons/book.svg?react";
import DraftIcon from "assets/icons/draft.svg?react";
import CaseIcon from "assets/icons/case.svg?react";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import * as openpgp from "openpgp";
import { AxiosProgressEvent } from "axios";
import Popper from "components/Popper";
import JSZip from "jszip";
import { IKey } from "dto/IKey";
import { logger } from "utils/logger";
import { useDownloadFileStore } from "store/downloadFile.store";
import { Tooltip } from "@mui/material";
import ModalsManagerContext from "contexts/ModalsManager";
import { gridRowsLookupSelector } from "@mui/x-data-grid-premium";
import usePlugin from "hooks/usePlugin";

const MenuCell: FC<Props> = ({
  element,
  refetchAll,
  caseId,
  handleFileDoubleClick,
  apiRef,
  handleCreateVersion,
  handleShare,
  handleCopy,
  handleCut,
  menuOnly = false,
  virtualElement,
  isTrash = false,
}) => {
  const { t } = useTranslation();
  const { setId, setProgress } = useDownloadFileStore();

  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const plugin = usePlugin();

  const { mutate: openEditor } = pluginService.useOpenEditor();
  const { mutate: getPluginVersion } = pluginService.useGetVersionMutated();
  const { mutate: addFileToDossier } = dossierService.useAddFile();
  const { mutateAsync: getFile } = fileService.useGetFile();
  const { mutate: deleteDossier } = dossierService.useDeleteDossier();
  const { mutate: restoreDossier } = dossierService.useRestoreDossier();

  const { dossierFiles, setDossierFiles } = useContext(DossierFilesContext);
  const { keys, setKeys } = useContext(KeysContext);
  const { setCert } = useContext(CertContext);

  const [showMenu, setShowMenu] = useState(menuOnly);
  const { mutate: setTemplate } = dossierService.useSetTemplate();
  const { mutate: setDesk } = dossierService.useSetDesk();
  const dropdownRef = useRef();

  const [showTags, setShowTags] = useState(false);
  const [showStatusLarge, setShowStatusLarge] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);

  const [, setShowDesk] = useState(false);
  const [, setShowTemplate] = useState(false);
  const [, setShowPluginError] = useState<boolean>(false);

  const canEdit = auth?.user?.profile?.role === "guest" ? false : true;
  const { modals, setModals } = useContext(ModalsManagerContext);

  const handleSetDesk = () => {
    setShowDesk(false);
    setDesk(
      {
        Items: [element.DossierId],
        Show: !element.DeskInfo_IsOn,
      },
      {
        onSuccess: () => {
          const files = [...dossierFiles];

          const el = files.find(
            (c) => c.Id === element.Id && c.Kind === element.Kind,
          );

          if (el) {
            el.DeskInfo_IsOn = !el.DeskInfo_IsOn;
          }

          setDossierFiles(files);
        },
      },
    );
  };

  const handleSetAsTemplate = () => {
    console.log("Handle set as template");
    setShowTemplate(false);
    setTemplate(
      {
        Items: [element.DossierId],
        IsTemplate: !element.IsTemplate,
      },
      {
        onSuccess: () => {
          apiRef.current.updateRows([
            { id: element.DossierId, IsTemplate: !element.IsTemplate },
          ]);
          setShowMenu(false);
        },
      },
    );
  };

  const onDownloadClick = async () => {
    if (element.Type === DossierItemType.Folder) {
      handleDownloadFolder();
      return;
    }

    setId(element.DossierId);
    setProgress(0);

    const fileResult = await getFile({
      //@ts-ignore
      id: element.SignedPdfUri ?? element.FileUri,
      ownerKind: element.Kind,
      callback(p: AxiosProgressEvent) {
        setProgress(p);
      },
    });

    logger.log("Get file completed");
    const key = keys.find((key: any) => key?.Id === element.EncryptionKeyId);

    if (!key && element.EncryptionKeyId) {
      console.log("Key not found", element);
      return;
    }

    let parentKey = null;
    //@ts-ignore
    if (key?.ParentKeyId) {
      parentKey = keys.find(
        //@ts-ignore
        (pkey: any) => pkey.Id === key.ParentKeyId,
      );
    }
    await downloadFile(
      userId,
      element.FileName,
      fileResult.data.Url,
      key,
      parentKey,
      (p: AxiosProgressEvent) => {
        console.log(p);
        setProgress(p);
      },
    );
    setId(null);
  };

  const onDeleteClick = () => {
    setShowMenu(false);
    deleteDossier(
      {
        Id: element.DossierId,
      },
      {
        onSuccess: () => {
          apiRef.current.updateRows([
            { id: element.DossierId, _action: "delete" },
          ]);
        },
        onError: () => refetchAll(),
      },
    );
  };

  const onRestoreClick = () => {
    setShowMenu(false);
    restoreDossier(
      {
        Id: element.DossierId,
      },
      {
        onSuccess: () => {
          // Po przywróceniu element znika z widoku kosza.
          apiRef.current.updateRows([
            { id: element.DossierId, _action: "delete" },
          ]);
        },
        onError: () => refetchAll?.(),
      },
    );
  };

  const handleEditLabelClick = () => {
    const rows = Object.values(
      gridRowsLookupSelector(apiRef),
    ) as IDossierItem[];

    const toUpdate = rows.find((x) => x.DossierId === element.DossierId);

    if (toUpdate) {
      toUpdate.editLabel = true;

      apiRef.current.setRows(rows);
    }
  };

  const onDownloadXades = async () => {
    const fileBlob = await GetFile(element?.SignedXadesUri ?? "");
    const blob = await getBlob(fileBlob.data.Url);

    const URL = window.URL.createObjectURL(new Blob([blob.data]));
    const link = document.createElement("a");

    link.href = URL;
    link.setAttribute("download", "xades.xml");
    document.body.appendChild(link);
    link.click();
  };

  const handleEdit = async (e: IDossierItem) => {
    getPluginVersion(
      {},
      {
        onSuccess: async (data) => {
          if (!data.IsConnected || plugin.isConnectionStale) {
            await plugin.connect();
          }
          logger.log("Got plugin version, opening editor", data);
          if (
            e.Kind === ItemKind.CaseFile ||
            e.Kind === ItemKind.EditorDocument
          ) {
            openEditor({
              Id: e.Id,
              Kind: e.Kind,
            });
          } else {
            createCopyOfDossierItem(e, true, (itm) => {
              openEditor({
                Id: itm.Id,
                Kind: itm.Kind,
              });
            });
          }
        },
        onError() {
          setShowPluginError(true);
        },
      },
    );

    setShowMenu(false);
  };

  const createCopyOfDossierItem = async (
    e: IDossierItem,
    replace: boolean,
    onSuccess: (item: IDossierItem) => void,
  ) => {
    const newDossierElements = [...dossierFiles];

    logger.log("Creating copy of", e);
    const itm = createDossierObject(
      element.Id + "copy",
      element.FileName,
      element.FileContentType,
      element.FileSize,
      element.ParentId,
      element.Label && !replace ? `${t("dossier.copyPrefix")} ${element.Label}` : element.Label,
      element.Type,
    );

    if (!replace) {
      newDossierElements.push(itm);
      setDossierFiles(newDossierElements);
    } else {
      //newDossierElements[newDossierElements.indexOf(e)] = itm;
    }

    const fileBlob = await GetFile(element?.FileUri ?? "");
    const blob = await getBlob(fileBlob.data.Url);

    const key = keys.find((key: any) => key?.Id === element?.EncryptionKeyId);

    if (!key) return;

    let parentKey = null;

    if (key.ParentKeyId) {
      parentKey = keys.find(
        //@ts-ignore
        (pkey: any) => pkey?.Id === key?.ParentKeyId,
      );
    }

    const decryptedFile = await decryptFile(
      userId ?? "",
      new Blob([blob.data]),
      key,
      parentKey,
    );

    const respo = await axiosInstance.get(
      `/case/keys?Id=${element.CaseId ?? caseId}`,
    );

    const casePublicKeyData = await readFileFromBase64(
      respo.data[0].PublicKey,
      "application/other",
    );

    const casePublicKey = await openpgp.key.read(
      new Uint8Array(casePublicKeyData),
    );

    setId(element.DossierId);
    setProgress(0);

    const r = await handleCreate(
      [decryptedFile],
      element.CaseId ?? caseId,
      casePublicKey,
      true,
      true,
      !replace ? addCopyToFileName(element.FileName ?? "") : element.FileName,
      element.FileContentType,
      (fileIndex: number, progress: number) => setProgress(progress),
    );

    if (!r) {
      return;
    }

    const payload: IDossierAddFile = {
      CaseId: element.CaseId ?? caseId,
      ParentId: element.ParentId,
      SourceId: element.DossierId,
      Replace: replace,
      Label:
        element.Label && !replace ? `${t("dossier.copyPrefix")} ${element.Label}` : element.Label,
      Files: [
        //@ts-ignore
        {
          KeyId: r[0].keyId,
          Uri: r[0].uri,
        },
      ],
    };
    addFileToDossier(payload, {
      onSuccess: (response) => {
        setKeys((prev) => [
          ...prev,
          ...(response.data?.ObjectKeys ?? []),
          ...(response.data?.Keys ?? []),
        ]);
        setId(null);
        const item = response.data.Items[0];

        newDossierElements.push(item);
        setDossierFiles(newDossierElements);
        onSuccess?.(item);
      },
    });
  };

  const handelEditAsCopy = async (e: IDossierItem) => {
    createCopyOfDossierItem(e, false, () => {
      refetchAll();
      // openEditor({
      //   Id: item.Id,
      //   Kind: item.Kind,
    });
  };

  const handleDelete = () => {
    const ask = localStorage.getItem("ask");

    if (ask === "true") {
      onDeleteClick();
      return;
    }

    setShowDelete(true);
  };

  const handleSign = (e: IDossierItem) => {
    if (e.Kind === ItemKind.CaseFile || e.Kind === ItemKind.EditorDocument) {
      setCert({ id: e.Id, name: e.FileName ?? "" });
      handleFileDoubleClick(e);
    } else {
      createCopyOfDossierItem(e, true, (item) => {
        const itm = item;
        setTimeout(
          (i) => {
            setCert({ id: i.Id, name: i.FileName ?? "" });
            handleFileDoubleClick(i);
          },
          500,
          itm,
        );
      });
    }
  };

  const handleAttachToCase = () => {
    localStorage.removeItem("package");
    localStorage.removeItem("packages");
    localStorage.setItem("dossierItem", JSON.stringify(element));
    setModals({ ...modals, attachCase: true, hide: false });
  };

  const handleGenerateLetters = () => {
    // Modal czyta dokument z localStorage("package") jako IPackage (toDocRef).
    const pack = {
      Id: element.Id,
      Case: { Id: element.CaseId ?? caseId },
      EncryptionKeyId: element.EncryptionKeyId,
      S1: element.Label ?? element.FileName,
    };
    localStorage.setItem("package", JSON.stringify(pack));
    setModals({ ...modals, generateLetters: true, hide: false });
    setShowMenu(false);
  };

  const [, setIsDownloading] = useState(false);
  let totalToDownload = 0;
  let downloaded = 0;
  const localKeys: IKey[] = [];

  const { mutateAsync: getDossier } = dossierService.useMutationGetItems();

  const handleDownloadFolder = async () => {
    setProgress(0);
    totalToDownload = 0;
    downloaded = 0;

    setIsDownloading(true);
    const zip = new JSZip();
    await travelFolderToMove(element, zip);

    const content = await zip.generateAsync({ type: "blob" });

    const URL = window.URL.createObjectURL(content);
    const link = document.createElement("a");
    link.href = URL;
    link.setAttribute("download", `${element.Label ?? t("dossier.noName")}.zip`);
    document.body.appendChild(link);
    link.click();

    setIsDownloading(false);
  };

  const travelFolderToMove = (folder: IDossierItem, zip: JSZip) => {
    return new Promise(async (resolve) => {
      const data = await getDossier({
        CaseId: caseId!,
        FolderId: folder.DossierId,
        Start: 0,
        End: 1000,
      });

      if (data?.data) {
        // @ts-ignore
        setKeys((prev) => [
          ...prev,
          ...data.data.ObjectKeys,
          ...data.data.Keys,
        ]);

        localKeys.push(...data.data.ObjectKeys);
        localKeys.push(...data.data.Keys);

        totalToDownload += data.data.Items.length;

        const folders = data.data.Items?.filter(
          (dossier: IDossierItem) => dossier.Kind === ItemKind.DossierFolder,
        );
        const directFiles = data.data.Items?.filter(
          (dossier: IDossierItem) => dossier.Kind !== ItemKind.DossierFolder,
        );

        for (const f of directFiles) {
          if (!f.FileUri) continue;

          const file = await GetFile(f.FileUri);
          const blob = await getBlob(file.data.Url);

          const key =
            keys.find((key) => key?.Id === f.EncryptionKeyId) ??
            localKeys.find((key) => key?.Id === f.EncryptionKeyId);

          if (!key) {
            continue;
          }

          let parentKey = null;
          //@ts-ignore
          if (key.ParentKeyId) {
            parentKey = keys.find(
              //@ts-ignore
              (pkey: any) => pkey?.Id === key?.ParentKeyId,
            );
          }

          try {
            const decryptedFile = await decryptFile(
              userId ?? "",
              new Blob([blob.data]),
              key,
              parentKey,
            );

            zip.file(f.FileName ?? t("dossier.noName"), decryptedFile);

            downloaded++;
            console.log(
              "Progress",
              (downloaded / totalToDownload) * 100,
              downloaded,
              totalToDownload,
            );
            setProgress(() => (downloaded / totalToDownload) * 100);
          } catch {}
        }

        for (const f of folders) {
          const zipFolder = zip.folder(f.Label ?? t("dossier.noName"));
          downloaded++;
          await travelFolderToMove(f, zipFolder!);
        }

        resolve(true);
      }
    });
  };

  // useEffect(() => {
  //   if (apiRef?.current) {
  //     apiRef.current.updateRows([{ id: element.DossierId, menu: showMenu }]);
  //   }
  // }, [showMenu]);

  return (
    <div className={styles.hover} ref={dropdownRef}>
      {!menuOnly && (
        <Tooltip title={t("common.more")}>
          <div>
            <FontAwesomeIcon
              onClick={(e) => {
                setShowMenu(!showMenu);

                e.preventDefault();
                e.stopPropagation();
              }}
              icon={faEllipsisVertical}
              className={styles.menuIcon}
            />
          </div>
        </Tooltip>
      )}

      {
        <Popper
          open={showMenu}
          anchorEl={dropdownRef?.current}
          virtualElement={virtualElement}
          setOpen={setShowMenu}
        >
          <div className={styles.dropdown}>
            {isTrash && (
              <div onClick={onRestoreClick} className={styles.dropdownItem}>
                <FontAwesomeIcon icon={faRotateLeft} title={t("dossier.restore")} />
                {t("dossier.restore")}
              </div>
            )}
            {element.CanEdit && (
              <div
                onClick={() => {
                  handleEdit(element);
                }}
                className={styles.dropdownItem}
              >
                <EditIcon title={t("common.edit")} />
                {t("common.edit")}
              </div>
            )}

            {!element.CaseId &&
              (element.Kind === ItemKind.CaseFile ||
                element.Kind === ItemKind.EditorDocument) && (
                <div
                  onClick={() => {
                    handleAttachToCase();
                  }}
                  className={styles.dropdownItem}
                >
                  <CaseIcon title={t("modals.linkToCase")} />
                  {t("modals.linkToCase")}
                </div>
              )}

            {/* {element.CanEditAsCopy && (
              <div
                onClick={() => {
                  handelEditAsCopy(element);
                }}
                className={styles.dropdownItem}
              >
                <EditIcon title="Edytuj jako kopię" />
                Edytuj jako kopię
              </div>
            )} */}

            <div
              onClick={() => {
                if (element.Type === DossierItemType.Folder) {
                  window.open(
                    `${import.meta.env.VITE_APP_SITE}/folder/${caseId}/${
                      element.Id
                    }`,
                    "_blank",
                  );
                } else {
                  const newUrl = `${
                    import.meta.env.VITE_APP_SITE ??
                    "https://alfa.pocztaprawnicza.pl"
                  }/document?pdf=${element.Id}:${element.FileName.replace(
                    ":",
                    "",
                  )}:${element.FileUri ?? element.Id}:${element.Kind}:${
                    element.EncryptionKeyId
                  }`;

                  window.open(newUrl, "_blank");
                }
              }}
              className={styles.dropdownItem}
            >
              <NewTabIcon title={t("tooltips.openInNewTab")} />{t("tooltips.openInNewTab")}
            </div>

            {element.CanEdit &&
              (element.Kind === ItemKind.CaseFile ||
                element.Kind === ItemKind.EditorDocument) && (
                <div
                  onClick={() => {
                    handleSetAsTemplate();
                  }}
                  className={styles.dropdownItem}
                >
                  <DraftIcon title={t("dossier.markAsTemplate")} />
                  {t("dossier.markAsTemplate")}
                </div>
              )}

            <div
              className={styles.dropdownItem}
              onClick={() => handleSetDesk()}
            >
              <DeskIcon title={t("dossier.pinToDesk")} />
              {t("dossier.pinToDesk")}
            </div>

            {element.Kind === ItemKind.CaseFile && canEdit && (
              <div
                onClick={(e) => {
                  setShowStatusLarge(!showStatusLarge);
                  e.stopPropagation();
                }}
                className={styles.dropdownItem}
              >
                <StatusIcon title={t("common.status")} />
                {t("common.status")}
              </div>
            )}

            {
              <div
                onClick={(e) => {
                  setShowTags(!showTags);
                  e.stopPropagation();
                }}
                className={styles.dropdownItem}
              >
                <LabelIcon title={t("common.label")} />
                {t("common.label")}
              </div>
            }
            <div className={styles.line}></div>

            {(element.Type === DossierItemType.Attachment ||
              element.Type === DossierItemType.Folder) && (
              <div
                onClick={() => {
                  onDownloadClick();
                  setShowMenu(false);
                }}
                className={styles.dropdownItem}
              >
                <DownloadIcon title={t("common.download")} />
                {t("common.download")}
              </div>
            )}
            {element.SignedXadesUri && (
              <div onClick={onDownloadXades} className={styles.dropdownItem}>
                <DownloadIcon title={t("dossier.downloadXades")} />
                {t("dossier.downloadXades")}
              </div>
            )}

            {canEdit && (
              <div
                onClick={() => {
                  handleEditLabelClick();
                  setShowMenu(false);
                }}
                className={styles.dropdownItem}
              >
                <EditIcon title={t("common.rename")} />
                {t("common.rename")}
              </div>
            )}
            {element.CanEditAsCopy && (
              <div
                onClick={() => {
                  handelEditAsCopy(element);
                  setShowMenu(false);
                }}
                className={styles.dropdownItem}
              >
                <CopyIcon title={t("dossier.createCopy")} />
                {t("dossier.createCopy")}
              </div>
            )}
            {element.CanEditAsCopy && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleCreateVersion(element);
                }}
                className={styles.dropdownItem}
              >
                <CopyIcon title={t("dossier.createVersion")} />
                {t("dossier.createVersion")}
              </div>
            )}

            <div className={styles.line}></div>
            {/* <div
              className={styles.dropdownItem}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleMove(element);
              }}
            >
              <MoveIcon title={t("common.move")} />
              {t("common.move")}
            </div> */}
            <div
              className={styles.dropdownItem}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleShare(element);
              }}
            >
              <ShareIcon title={t("common.share")} />
              {t("common.share")}
            </div>

            <div className={styles.line}></div>
            {canEdit && element.Type !== DossierItemType.Folder && (
              <div
                onClick={() => {
                  handleSign(element);
                }}
                className={styles.dropdownItem}
              >
                <SignIcon title={t("dossier.sign")} />
                {t("dossier.sign")}
              </div>
            )}
            {element.Type !== DossierItemType.Folder && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowTransfer(true);
                  setShowMenu(false);
                }}
                className={styles.dropdownItem}
              >
                <AcceptanceSignIcon title="Do akceptacji/podpisu" />
                Do akceptacji/podpisu
              </div>
            )}
            {element.Type !== DossierItemType.Folder &&
              (element.Kind === ItemKind.CaseFile ||
                element.Kind === ItemKind.EditorDocument) && (
                <div
                  onClick={handleGenerateLetters}
                  className={styles.dropdownItem}
                >
                  <LettersIcon title="Generuj pisma" />
                  Generuj pisma
                </div>
              )}
            <div className={styles.line}></div>
            {handleCopy && (
              <div
                onClick={() => handleCopy(element)}
                className={styles.dropdownItem}
              >
                <CopyIcon2 title={t("common.copy")} />
                {t("common.copy")}
              </div>
            )}
            {handleCopy && (
              <div
                onClick={() => handleCut(element)}
                className={styles.dropdownItem}
              >
                <CutIcon title={t("common.cut")} />
                {t("common.cut")}
              </div>
            )}
            {canEdit && !isTrash && (
              <div
                onClick={() => handleDelete()}
                className={styles.dropdownItem}
              >
                <BinIcon title={t("common.delete")} />
                {t("common.delete")}
              </div>
            )}
          </div>
        </Popper>
      }

      {showDelete && (
        <ConfirmModal
          title={t("confirm.deleteTitle")}
          text={t("confirm.deleteSelectedItem")}
          acceptLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onAccept={() => {
            setShowDelete(false);
            onDeleteClick();
          }}
          onCancel={() => setShowDelete(false)}
        />
      )}

      {showTransfer && (
        <TransferToAcceptanceModal
          file={{
            title: element.Label || element.FileName || "Plik",
            fileName: element.FileName,
            fileUri: element.FileUri,
            encryptionKeyId: element.EncryptionKeyId,
            documentType: "dossier",
            documentId: element.DossierId,
          }}
          onClose={() => setShowTransfer(false)}
        />
      )}
    </div>
  );
};

export default MenuCell;
